import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ClearSearchCriteria } from '../../store/actions/records.actions';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { SetInstanceDataTableRecordSelect, setInstanceDataTableScroll } from '../../store/actions/instances.actions';
import { TableColumn } from '../../interface/table-column.interface';
import { TableData } from '../../interface/table-data.interface';
import { TableRecordAction } from '../../interface/table-record-action.interface';
import { UtilsService } from '../../services/utils/utils.service';

@Component({
    selector: 'app-gst-instance-data-table',
    templateUrl: './instance-data-table.component.html',
    styleUrls: ['./instance-data-table.component.scss'],
    standalone: false,
})
export class InstanceDataTableComponent implements OnInit, OnChanges {
    @Input() aggregationsTemplate: TemplateRef<any>;
    @Input() demoMode = false;
    @Input() templateId: string;
    @Input() data: BehaviorSubject<TableData[]>;
    @Input() allTableColumns: BehaviorSubject<TableColumn[]>;
    @Input() selectedColumns: string[] = [];
    @Input() noRecordMsg: string;
    @Input() columnSearchRequired = false;
    @Input() loading: boolean;
    @Input() recordActions: TableRecordAction[];
    @Input() noLog = false;
    @Input() hideTableColumns;
    @Input() isMisMatchGroup: boolean;
    @Input() onClearSearch: boolean;
    @Input() canSelectMultiple: boolean;
    @Input() useUserPreferences = false;

    @Output() clickedRecord = new EventEmitter<TableData>();
    @Output() onSearchEvent = new EventEmitter();
    @Output() onColumnChange = new EventEmitter<{ columns: string[]; source: string }>();
    @Output() onResetPreferences = new EventEmitter();

    @ViewChild('sourceTable') sourceTable: ElementRef;

    private unSubscribe = new Subject();
    public columnKeyPair: { [property: string]: TableColumn } = {};
    public columnTypePair: { [property: string]: string } = {};
    public allColumns: TableColumn[] = [];
    public clonedColumns: string[] = [];
    public dataSource = new MatTableDataSource([]);
    public get visibleColumns(): TableColumn[] {
        const filtered = this.allColumns.filter(column => {
            const matchesSearch =
                !this.searchColumns ||
                this.searchColumns.length === 0 ||
                (this.searchColumns.length > 0 && column.name.toLowerCase().indexOf(this.searchColumns.toLowerCase()) > -1);
            const notActionColumn = this.actionColumns.indexOf(column.id) === -1;
            const notHidden = !(this.hideTableColumns && this.hideTableColumns.indexOf(column.id) > -1);
            const notHiddenInAllColumnsList = !column.hideInAllColumnsList;
            return matchesSearch && notActionColumn && notHidden && notHiddenInAllColumnsList;
        });
        // Order by clonedColumns to maintain drag-drop order
        return filtered.sort((a, b) => {
            const indexA = this.clonedColumns.indexOf(a.id);
            const indexB = this.clonedColumns.indexOf(b.id);
            // Put items in clonedColumns first, in their order
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return 0;
        });
    }
    public expandedElement;
    public noRecordsFound = false;
    private throwAtLast = ['table-actions', 'log'];
    public actionColumns = ['table-actions', 'log', 'table-checkbox'];
    public selectAll;
    public actionVisibility: { [property: string]: { [property: string]: boolean } } = {};
    public selectAllRecords = false;
    public searchColumns: string;
    private syncScrolling: boolean;
    private scrollTimeout: any;
    public searchColumnControl = new UntypedFormControl();
    private enteredSearchValue = '';
    public selectedRecordsCount: number;
    public table1_totalCount: number;
    public table2_totalCount: number;

    constructor(
        private _libUtils: UtilsService,
        private _commonUtils: CommonUtilsService,
        private store$: Store,
        private actions$: Actions
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedColumns) {
            this.pushSpecialColumns();
            // Sync clonedColumns with the new selectedColumns
            this.buildClonedColumns();
            this.checkFiltersSelected();
        }
    }

    private clearsearch = () => {
        this.searchColumnControl = new UntypedFormControl();
        this.enteredSearchValue = '';
    };

    public trackRecord = (item: any) => (item as TableData)?.recordId;

    public searchInputModelChange = (column: string) => {
        if (this.searchColumnControl.value.trim() !== '' && this._libUtils.invalidChars(this.searchColumnControl.value)) {
            this._libUtils.alertError('Special characters are not allowed');
            return;
        }
        this.enteredSearchValue = this.searchColumnControl.value ? this.searchColumnControl.value : '';
        const data = {
            name: column,
            value: this.enteredSearchValue,
        };
        this.onSearchEvent.emit(data);
        if (!this.enteredSearchValue) {
            this.searchColumnControl = new UntypedFormControl();
            this.enteredSearchValue = '';
        }
    };

    private checkColumnsCount = () => {
        if (this.allColumns && this.actionColumns) {
            const filteredColumns = this.allColumns && this.allColumns.filter(column => this.actionColumns.indexOf(column.id) === -1);
            return filteredColumns && filteredColumns.length >= 5;
        }
        return true;
    };

    private pushSpecialColumns = () => {
        this.throwAtLast
            .filter(columnName => this.selectedColumns.indexOf(columnName) === -1)
            .forEach(columnName => {
                if (columnName === 'table-actions') {
                    if (this.recordActions && this.recordActions.length > 0) {
                        this.selectedColumns.push(columnName);
                    }
                } else if (columnName === 'log') {
                    !this.noLog && this.selectedColumns.push(columnName);
                } else if (columnName === 'SELECTRECORD') {
                    this.selectedColumns.unshift(columnName);
                } else {
                    this.selectedColumns.push(columnName);
                }
            });
        const filterActionIndex = this.selectedColumns.indexOf('dynamicActions');
        if (filterActionIndex !== -1) {
            return this.selectedColumns.splice(filterActionIndex, 1);
        }
        this.allColumns
            .filter(column => column.hideInAllColumnsList && !column.hide)
            .filter(column => this.selectedColumns.indexOf(column.id) === -1)
            .forEach(column => this.selectedColumns.unshift(column.id));
        this.generateColumnTypes();
    };

    private generateColumnTypes = () => {
        this.columnTypePair = {};
        this.selectedColumns.forEach(columnId => {
            this.columnTypePair[columnId] = this.getColumnType(columnId);
        });
    };

    private getColumnType = (columnId: string): string => {
        const column = this.getColumn(columnId);
        return column && column.type;
    };

    private getColumn = (columnId: string): TableColumn => {
        const column = this.allColumns.find(tableColumn => tableColumn.id === columnId);
        if (column) {
            return column;
        }
        const throwAtLastColumn = this.throwAtLast.indexOf(columnId) > -1;
        if (throwAtLastColumn) {
            if (columnId === 'log') {
                return {
                    id: columnId,
                    hide: false,
                    hideInAllColumnsList: true,
                    icon: undefined,
                    name: 'Log',
                    type: 'ALL_COLUMNS_LIST',
                    options: undefined,
                };
            } else if (columnId === 'table-actions') {
                return {
                    id: columnId,
                    hide: false,
                    hideInAllColumnsList: true,
                    icon: undefined,
                    name: 'Actions',
                    type: 'ACTIONS',
                    options: undefined,
                };
            }
        }
        return;
    };

    private buildClonedColumns = () => {
        this.clonedColumns = this.selectedColumns
            .filter(columnId => {
                const column = this.allColumns.find(columnRecord => columnRecord.id === columnId);
                return column && !column.hideInAllColumnsList;
            })
            .slice(0);
    };

    private checkFiltersSelected = () => {
        const leftOutColumns =
            this.allColumns &&
            this.allColumns
                .filter(column => !column.hideInAllColumnsList && !column.hide)
                .filter(column => !this.hideTableColumns || (this.hideTableColumns && this.hideTableColumns.indexOf(column.id) === -1))
                .filter(column => this.clonedColumns && this.clonedColumns.indexOf(column.id) === -1);
        this.selectAll = leftOutColumns && leftOutColumns.length === 0;
    };

    private buildActionsShowHideModal = (records: TableData[]) => {
        this.actionVisibility = {};
        (records || []).forEach(record => {
            this.actionVisibility[record.recordId] = {};
            (this.recordActions || []).forEach(action => {
                this.actionVisibility[record.recordId][action.displayName] = this.checkForDynamicActions(record, action);
            });
        });
    };

    private checkForDynamicActions = (element, action) => {
        if (element && element.dynamicActions && element.dynamicActions.length > 0) {
            const dynamicAction = element.dynamicActions;
            for (let i = 0; i < dynamicAction.length; i++) {
                if (dynamicAction[i].erroneous === false) {
                    if (dynamicAction[i].displayName === action.displayName) {
                        return false;
                    }
                }
            }
        }
        return true;
    };

    private checkAllRecordsSelected = () => {
        const nonSelectedRecords = this.getNonSelectedRecords();
        this.selectAllRecords = nonSelectedRecords.length === 0 && this.dataSource.data.length > 0;
    };

    public onSelectAllRecords = () => {
        this.toggleRecordsSelection();
        this.triggerRelatedTableSelection();
        this.setSelectedRecordsCount();
    };

    private toggleRecordsSelection = () => {
        this.data.value
            ?.filter(record =>
                this.isMisMatchGroup
                    ? (this.templateId === 'source1' && !record.hideSource1Checkbox) ||
                      (this.templateId === 'source2' && !record.hideSource2Checkbox)
                    : true
            )
            .filter(record => record.recordId !== undefined)
            .forEach(record => {
                record.selected = this.selectAllRecords;
            });
    };

    public onRecordSelectionChange = (changedRecord: TableData) => {
        const associationId = changedRecord.mainAssociationId as string;
        associationId &&
            this.data.value.filter(row => row.mainAssociationId === associationId).forEach(row => (row.selected = changedRecord.selected));
        this.checkAllRecordsSelected();
        this.triggerRelatedTableSelection();
        this.setSelectedRecordsCount();
    };

    private triggerRelatedTableSelection = () => {
        const selectedRecordIds = [];
        const associationIds = [];
        this.data.value
            .filter(record => record.selected)
            .forEach(record => {
                selectedRecordIds.push(record.recordId);
                record.mainAssociationId && associationIds.push(record.mainAssociationId);
            }, []);
        this.store$.dispatch(
            SetInstanceDataTableRecordSelect({
                templateId: this.templateId,
                allSelected: this.selectAllRecords,
                recordIds: selectedRecordIds,
                associationIds,
            })
        );
    };

    private getNonSelectedRecords = (): any[] => this.dataSource.data.filter(record => record.recordId && !record.selected);

    public getSelectedRecords = (): any[] =>
        this.dataSource.data
            .filter(record => record.recordId && record.selected)
            .filter(record =>
                this.isMisMatchGroup
                    ? (this.templateId === 'source1' && !record.hideSource1Checkbox) ||
                      (this.templateId === 'source2' && !record.hideSource2Checkbox)
                    : true
            );

    private setSelectedRecordsCount = () => (this.selectedRecordsCount = this.getSelectedRecords().length);

    public selectAllFilters = selectedAll => {
        const oldColumns = this.clonedColumns.slice(0);
        const actionColumns = this.actionColumns.filter(column => this.throwAtLast.indexOf(column) === -1);
        this.clonedColumns = oldColumns.filter(column => actionColumns.indexOf(column) > -1);
        if (selectedAll) {
            this.allColumns
                .filter(column => !column.hideInAllColumnsList && !column.hide)
                .filter(
                    column =>
                        this.actionColumns.indexOf(column.id) === -1 &&
                        !(this.hideTableColumns && this.hideTableColumns.indexOf(column.id) > -1)
                )
                .forEach(column => {
                    this.clonedColumns.push(column.id);
                });
        }
    };

    public cloneSubmit = () => {
        const actionColumns = this.actionColumns.filter(column => this.throwAtLast.indexOf(column) === -1);
        const newColumns = this.clonedColumns.filter(column => {
            if (actionColumns.indexOf(column) === -1 || column === 'table-checkbox') {
                return column;
            }
        });
        const tempColumns = newColumns.filter(column => this.throwAtLast.indexOf(column) === -1);
        const hiddenColumns = [];
        this.allColumns
            .filter(column => column.hideInAllColumnsList && this.selectedColumns.indexOf(column.id) > -1)
            .forEach(column => hiddenColumns.push(column.id));
        if (tempColumns && tempColumns.length > 0) {
            if (tempColumns.length === 1 && tempColumns[0] === 'table-checkbox') {
                this._libUtils.alertError('Select at least one column name');
            } else {
                const newTempColumns = [...hiddenColumns, ...tempColumns];
                this.selectedColumns.splice(0);
                newTempColumns && newTempColumns.forEach(column => this.selectedColumns.push(column));
                this.pushSpecialColumns();
                // Emit column change event - filter out internal columns
                const internalColumns = ['SELECTRECORD', 'table-actions', 'log', 'table-checkbox'];
                const columnsToEmit = this.selectedColumns.filter(col => !internalColumns.includes(col));
                this.onColumnChange.emit({ columns: columnsToEmit, source: this.templateId });
            }
        } else {
            this._libUtils.alertError('Select at least one column name');
        }
        if (this.selectAll) {
            this._commonUtils.setInStorage('filterColumnLog', true);
        } else {
            this._commonUtils.setInStorage('filterColumnLog', false);
        }
    };

    public onColumnDrop = (event: CdkDragDrop<TableColumn[]>) => {
        const prevColumn = this.visibleColumns[event.previousIndex];
        const currColumn = this.visibleColumns[event.currentIndex];

        // Only move if both items are in clonedColumns
        if (prevColumn && currColumn && this.clonedColumns.includes(prevColumn.id) && this.clonedColumns.includes(currColumn.id)) {
            const prevIndex = this.clonedColumns.indexOf(prevColumn.id);
            const currIndex = this.clonedColumns.indexOf(currColumn.id);
            moveItemInArray(this.clonedColumns, prevIndex, currIndex);
        }
    };

    public trackByAllColumnsMethod = (_index: number, columnId: any): number => {
        return columnId;
    };

    public checkboxSelect = (checked: boolean, column: TableColumn) => {
        if (checked) {
            let index = this.allColumns.findIndex(columnItem => columnItem.id === column.id);
            if (index !== -1) {
                if (this.allColumns.findIndex(columnInAllColumns => columnInAllColumns.type === 'SELECTRECORD') > -1) {
                    index++;
                }
                this.clonedColumns.splice(index, 0, column.id);
            } else {
                this.clonedColumns.push(column.id);
            }
        } else {
            const index = this.clonedColumns.indexOf(column.id);
            this.clonedColumns.splice(index, 1);
        }
        if (this.actionColumns.length !== 0) {
            this.actionColumns.forEach(eachAction => {
                const actionIndex = this.clonedColumns.indexOf(eachAction);
                if (actionIndex !== -1) {
                    this.clonedColumns.splice(actionIndex, 1);
                }
            });
        }
        this.checkFiltersSelected();
    };

    public trackByActionsMethod = (_index: number, action: any): number => {
        return action.displayName;
    };

    public onScroll = (event: any, left: number, top: number) => {
        event.preventDefault();
        if (this.syncScrolling || this.isMisMatchGroup) {
            return;
        }
        this.store$.dispatch(setInstanceDataTableScroll({ templateId: this.templateId, left, top }));
    };

    ngOnInit(): void {
        this.actions$.pipe(ofType(ClearSearchCriteria), takeUntil(this.unSubscribe)).subscribe(() => {
            this.clearsearch();
        });

        this.actions$.pipe(ofType(setInstanceDataTableScroll), takeUntil(this.unSubscribe)).subscribe(data => {
            if (this.templateId !== data.templateId) {
                this.syncScrolling = true;
                this.sourceTable.nativeElement.scrollTop = data.top;
                this.sourceTable.nativeElement.scrollLeft = data.left;
                this.sourceTable.nativeElement.scrollIntoView({ behavior: 'smooth' });
                clearTimeout(this.scrollTimeout);
                this.scrollTimeout = setTimeout(() => {
                    this.syncScrolling = false;
                }, 100);
            }
        });

        this.actions$
            .pipe(
                ofType(SetInstanceDataTableRecordSelect),
                takeUntil(this.unSubscribe),
                filter(() => !this.isMisMatchGroup)
            )
            .subscribe(data => {
                if (this.templateId !== data.templateId) {
                    this.selectAllRecords = data.allSelected;
                    this.toggleRecordsSelection();
                    if (!data.allSelected) {
                        this.data.value.forEach(record => {
                            record.selected =
                                data.recordIds.includes(record.recordId) ||
                                (record.mainAssociationId && data.associationIds.includes(record.mainAssociationId as string));
                        });
                    }
                    this.dataSource.data = this.data.value;
                    this.setSelectedRecordsCount();
                }
            });

        this.allTableColumns.pipe(takeUntil(this.unSubscribe)).subscribe(columns => {
            this.pushSpecialColumns();
            this.columnKeyPair = {};
            this.allColumns = columns;
            this.allColumns.forEach(column => {
                this.columnKeyPair[column.id] = column;
            });
            this.buildClonedColumns();
            this.checkFiltersSelected();
        });
        if (this.selectedColumns && this.checkColumnsCount()) {
            this.selectedColumns.indexOf('log') === -1 && this.selectedColumns.push('log');
            !this.demoMode && this.selectedColumns.indexOf('SELECTRECORD') === -1 && this.selectedColumns.unshift('SELECTRECORD');
        }
        this.generateColumnTypes();
        this.data.pipe(takeUntil(this.unSubscribe)).subscribe(data => {
            this.selectedRecordsCount = 0;
            if (data?.length > 0) {
                this.buildActionsShowHideModal(data);
                this.dataSource.data = data || [];
                this.checkAllRecordsSelected();
                this.dataSource.filterPredicate = (tableData, filter) => {
                    return this.selectedColumns.some(ele => {
                        return (
                            ele !== 'table-actions' &&
                            ele !== 'log' &&
                            tableData[ele] &&
                            tableData[ele].toLocaleLowerCase().indexOf(filter) !== -1
                        );
                    });
                };
                this.setSelectedRecordsCount();
            } else if (!data || data.length === 0) {
                if (this.dataSource.data.length > 0) {
                    this.buildActionsShowHideModal([]);
                    this.dataSource.data = [];
                    this.checkAllRecordsSelected();
                }
            }
        });
    }

    ngOnDestroy() {
        clearTimeout(this.scrollTimeout);
        this.unSubscribe.next(undefined);
        this.unSubscribe.complete();
    }
}
